import Component from '@glimmer/component'
import CellTooltip from 'teacher-ui-client/components/stable-table/cell-tooltip'
import { isHTMLSafe } from '@ember/template'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { getCellValue, type CustomCellSignature } from 'teacher-ui-client/components/stable-table/basic-table/cell'

export default class SortableCell extends Component<CustomCellSignature> {
  get config() {
    return this.args.tableMeta.config
  }

  get column() {
    return this.args.tableMeta.columnObject
  }

  get cell() {
    return this.args.tableMeta.cellObject
  }

  get value() {
    const value = getCellValue(this.args.tableMeta)
    return typeof value === 'string' || isHTMLSafe(value) ? value : ''
  }

  /**
   * If a sortKey is specified, use that for sort column identification. Otherwise, valuePath is usually fine
   */
  get sortKey() {
    return this.column?.sortKey || this.column?.valuePath
  }

  get sortColumns() {
    return this.config?.sortingConfig?.columns
  }

  /**
   * Find this column in the list of sortColumns. If it's not found, then this column is not sorted
   */
  get sortColumn() {
    const { sortKey, sortColumns } = this
    if (sortColumns && sortKey) {
      return sortColumns.find((item) => item.key === sortKey)
    }
    return null
  }

  /**
   * True if this column was the last to be clicked, and therefore is the last column to be sorted. This will always
   * show a sorted icon.
   */
  get isLatestSort() {
    const { sortColumns, sortColumn } = this

    if (sortColumns && sortColumn) {
      return sortColumns.indexOf(sortColumn) === 0
    }
    return false
  }

  /**
   * If this column is sorted (ie has a sortColumn) return the appropriate sort icon
   *
   * config.multipleSortIcons allows for multiple columns to show a sort arrow icon to indicate the multiple levels of
   * sorting. This is typically not enabled as the UI can be hard to understand - maybe we can enable this when we think
   * of a better way to display the sort priority.
   */
  get sortIcon() {
    const { sortColumn, isLatestSort } = this

    let sortIcon = 'sort'

    const showSortedIcon = sortColumn && (isLatestSort || this.config?.multipleSortIcons)
    if (showSortedIcon) {
      sortIcon = sortColumn.sortDirection === 'asc' ? 'sort-up' : 'sort-down'
    }

    return sortIcon
  }

  /**
   * Send the sort key upstream to onSortClick
   */
  onClick = () => {
    if (!this.sortKey) return
    this.config?.onSortClick?.({ sortKey: this.sortKey })
  }

  <template>
    <div
      data-test-cell-sortable
      class="flex flex-nowrap items-center print:inline"
      role="button"
      ...attributes
      {{on "click" this.onClick}}
    >
      {{! The purpose of the double span is to allow the text to wrap, while keeping the (optional) tooltip attached to
  the last word, even when the text wraps. The tooltip icon should never wrap by itself onto a new line }}
      {{! Set adjacentIcon true to have sort icon placed next to text. Else, sort icon is placed at end of cell. }}
      <span class="whitespace-nowrap {{unless this.cell.adjacentIcon 'w-full'}}">
        <span class="whitespace-normal">
          {{this.value}}
        </span>
        {{! template-lint-disable no-bare-strings }}
        {{#if this.cell.tooltipValue}}
          {{! This is a zero-width non-breaking space }}
          &#8203;
          <CellTooltip data-test-cell-tooltip class="ml-1" @text={{this.cell.tooltipValue}} role="presentation" />
        {{/if}}
      </span>
      {{#if (eq this.sortIcon "sort-up")}}
        <PhosphorIcon @icon="phosphor-caret-up-fill" class="ml-2" role="presentation" />
      {{else if (eq this.sortIcon "sort-down")}}
        <PhosphorIcon @icon="phosphor-caret-down-fill" class="ml-2" role="presentation" />
      {{else}}
        <PhosphorIcon @icon="phosphor-caret-up-down-fill" class="ml-2" role="presentation" />
      {{/if}}
    </div>
  </template>
}
